/**
 * Pacote 3 · P3-28 a P3-32 e P3-42 (D5 aprovada; mockups M6a/M6b) — Locação:
 *  - regra de cobrança por parceiro (editor no cadastro do parceiro; quadro na OL) — o desconhecido é "A confirmar";
 *  - acompanhamento da OL: etapas como fatos com data, próxima ação e pendência, dossiê da PO/OL (documentos
 *    restritos, sem cópia), remessas e parcelas (informada · prevista pela regra · a confirmar), pagamento do Cliente
 *    e recebimento da Inttrix distintos, com parcial e estorno.
 * "Emitida" continua sendo só o estado do documento: não prova crédito, assinatura nem locação ativa.
 */
import { useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { CAMPOS_TEXTO_REGRA, ETAPAS_REGISTRAVEIS, ITENS_DOSSIE, regraCobrancaVazia, ROTULO_ETAPA, type IdEtapaLocacao, type RegraCobrancaLocacao } from '../../../backend/src/compartilhado/locacao.ts';
import { dataBr } from '../../../backend/src/compartilhado/oportunidades.ts';
import { hojeIso } from './CampoData.tsx';
import { Ajuda, Estado, EstadoCarga, Modal, RolagemIndicada, useCarga } from './ui.tsx';
import { moeda2 } from './proposta/formatos.ts';
import { navegar } from '../rotas.ts';

/* ================= regra de cobrança ================= */

const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const comDia = (d: string) => `${dataBr(d).slice(0, 5)} (${SEMANA[new Date(`${d}T12:00:00Z`).getUTCDay()]})`;

function SeloP({ texto, tom }: { texto: string; tom: 'ok' | 'pend' | 'ni' | 'alerta' }) {
  return <span className={`selo-previa ${tom === 'alerta' ? 'pend' : tom}`}>{texto}</span>;
}

export function QuadroRegraCobranca({ regra, titulo, origem }: { regra: RegraCobrancaLocacao | null; titulo: string; origem?: 'proposta' | 'parceiro' | 'nenhuma' }) {
  const r = regra ?? regraCobrancaVazia();
  const ac = <SeloP texto="A confirmar" tom="pend" />;
  const pares: [string, ReactNode][] = [
    ['1ª emissão', r.diasEmissaoAposRemessa === null ? ac : `${r.diasEmissaoAposRemessa} dias após a remessa${r.emissaoFimDeSemanaProximoDiaUtil === true ? ' (fim de semana ou feriado: 1º dia útil seguinte)' : r.emissaoFimDeSemanaProximoDiaUtil === false ? ' (não se desloca)' : ''}`],
    ['1º vencimento', r.diasVencimentoAposEmissao === null ? ac : `${r.diasVencimentoAposEmissao} dias corridos após a emissão`],
    ['Vencimentos seguintes', r.vencimentosSeguintesMesmoDia ? 'Mesma data, nos meses seguintes' : ac],
    ...CAMPOS_TEXTO_REGRA.map(({ chave, rotulo }) => [rotulo, (r[chave] as string) || ac] as [string, ReactNode]),
  ];
  return (
    <section className="bloco-impacto" data-teste="regra-cobranca">
      <h3>
        {titulo}
        <Ajuda titulo="Regra de cobrança">A regra é do parceiro (contrato/minuta), não um padrão universal. O que o contrato não define fica “A confirmar”: o HUB não inventa prazo, data, desconto ou percentual. As datas efetivas registradas prevalecem sobre a previsão.</Ajuda>
      </h3>
      {origem === 'parceiro' && <p className="texto-secundario sem-margem">Regra do cadastro atual do parceiro (a proposta aceita não trouxe a regra).</p>}
      {origem === 'nenhuma' && <p className="texto-secundario sem-margem">Parceiro sem regra de cobrança cadastrada: tudo fica “A confirmar”.</p>}
      <dl className="pares-regra">
        {pares.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Editor no cadastro do parceiro de Locação (P3-28). Vazio = "A confirmar". */
export function EditorRegraCobranca({ valor, aoMudar }: { valor: RegraCobrancaLocacao; aoMudar: (r: RegraCobrancaLocacao) => void }) {
  const num = (s: string) => (s.trim() === '' ? null : Math.max(0, Math.floor(Number(s))));
  const tri = (v: boolean | null) => (v === null ? '' : v ? 'sim' : 'nao');
  const deTri = (s: string) => (s === '' ? null : s === 'sim');
  return (
    <div className="largura-total editor-regra-cobranca" data-teste="editor-regra-cobranca">
      <div className="grade-3">
        <label className="campo">
          1ª emissão: dias após a remessa
          <input inputMode="numeric" value={valor.diasEmissaoAposRemessa ?? ''} placeholder="A confirmar" onChange={(e) => aoMudar({ ...valor, diasEmissaoAposRemessa: num(e.target.value) })} data-teste="regra-dias-emissao" />
        </label>
        <label className="campo">
          1ª emissão em fim de semana ou feriado
          <select value={tri(valor.emissaoFimDeSemanaProximoDiaUtil)} onChange={(e) => aoMudar({ ...valor, emissaoFimDeSemanaProximoDiaUtil: deTri(e.target.value) })} data-teste="regra-fim-de-semana">
            <option value="">A confirmar</option>
            <option value="sim">1º dia útil seguinte</option>
            <option value="nao">Não se desloca</option>
          </select>
        </label>
        <label className="campo">
          1º vencimento: dias corridos após a emissão
          <input inputMode="numeric" value={valor.diasVencimentoAposEmissao ?? ''} placeholder="A confirmar" onChange={(e) => aoMudar({ ...valor, diasVencimentoAposEmissao: num(e.target.value) })} data-teste="regra-dias-vencimento" />
        </label>
        <label className="campo">
          Vencimentos seguintes
          <select value={valor.vencimentosSeguintesMesmoDia ? 'sim' : ''} onChange={(e) => aoMudar({ ...valor, vencimentosSeguintesMesmoDia: e.target.value === 'sim' ? true : null })} data-teste="regra-seguintes">
            <option value="">A confirmar</option>
            <option value="sim">Mesma data, nos meses seguintes</option>
          </select>
        </label>
        {CAMPOS_TEXTO_REGRA.map(({ chave, rotulo }) => (
          <label key={chave} className="campo">
            {rotulo}
            <input value={valor[chave] as string} placeholder="A confirmar" maxLength={600} onChange={(e) => aoMudar({ ...valor, [chave]: e.target.value })} data-teste={`regra-${chave}`} />
          </label>
        ))}
      </div>
    </div>
  );
}

/* ================= acompanhamento da OL ================= */

type Origem = 'informada' | 'prevista' | 'a_confirmar';
interface DataP {
  data: string | null;
  origem: Origem;
  pendencia?: string;
}
interface Mov {
  id: string;
  tipo: 'pagamento_cliente' | 'recebimento_inttrix';
  data: string;
  valor: number;
  observacao: string;
  autor: string;
  estorno: { autor: string; justificativa: string } | null;
}
interface Parcela {
  numero: number;
  emissao: DataP;
  vencimento: DataP;
  aluguel: number;
  margemPrevista: number;
  situacao: string;
  pago: number;
  recebido: number;
  saldo: number;
  movimentos: Mov[];
  /** Contas a Receber E1: dados do Financeiro (só para quem tem acesso ao Receber; nulo para os demais) */
  financeiro: FinanceiroParcela | null;
}
/** Contas a Receber E1: o que o Financeiro registrou para a parcela (esta tela só exibe). */
interface FinanceiroParcela {
  comissao: string;
  item: string;
  estimado: number | null;
  confirmado: number | null;
  pagamentosCliente: { data: string; valor: number }[];
  liberacao: string;
  disponibilidade: string | null;
  tom: 'ok' | 'pend' | 'ni' | 'ext';
}
export interface Acompanhamento {
  numero: string;
  situacaoDocumento: string;
  pedido: string;
  oportunidadeId: string;
  cliente: { nome: string; razao: string; cnpj: string };
  locadora: { nome: string; razao: string; cnpj: string } | null;
  contrato: { mensalidade: number; prazoMeses: number; periodicidade: string; cobrancas: number; percentual: number; margemParcela: number };
  regra: RegraCobrancaLocacao | null;
  origemRegra: 'proposta' | 'parceiro' | 'nenhuma';
  etapas: { id: IdEtapaLocacao; rotulo: string; data: string | null; situacao: 'concluida' | 'em_andamento' | 'nao_informada' }[];
  etapaAtual: string;
  tomEtapa: 'ok' | 'pend' | 'alerta' | 'ni';
  registrosEtapa: { id: string; etapa: IdEtapaLocacao; rotulo: string; data: string; observacao: string; autor: string; anulada: { autor: string; motivo: string } | null }[];
  proximaAcao: { descricao: string; data: string; responsavelId: string; responsavel: string } | null;
  pendencia: string | null;
  remessas: { id: string; nf: string; dataRemessa: string; valorParcela: number; quantidade: number; observacao: string; anulada: { motivo: string } | null; parcelas: Parcela[] }[];
  dossie: { id: string; rotulo: string; exigidos: number; vinculados: number; documentos: { id: string; nome: string; inativo: boolean }[] }[];
  documentosRestritos: { id: string; nome: string }[];
  podeVerRestrito: boolean;
  referencias: ReferenciasCliente;
  /** Contas a Receber E1: nulo = sem acesso ao Receber (as colunas do Financeiro não aparecem) */
  financeiro: { comissao: string | null; projeto: FinanceiroParcela | null } | null;
}
/** P4 bloco 3 (I15): Referências do Cliente da OL. */
export interface ReferenciasCliente {
  contratoSocial: { id: string; nome: string; restrito: boolean; inativo: boolean }[];
  procuradores: { entidadeId: string | null; unidade: string; nome: string; email: string }[];
  testemunhas: { nome: string; email: string }[];
  comerciais: { empresa: string; telefone: string; email: string }[];
  bancaria: { banco: string; contato: string; telefone: string; email: string } | null;
  preenchidas: boolean;
  faltas: string[];
  atualizadas: { em: string } | null;
  opcoes: { unidades: { id: string; rotulo: string }[]; contatos: { nome: string; email: string }[]; documentos: { id: string; nome: string; restrito: boolean }[] };
}

const pctBr = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

function CelulaData({ d }: { d: DataP }) {
  if (!d.data) return <SeloP texto={d.pendencia?.includes('mês sem o dia') ? 'A confirmar (mês sem o dia)' : 'A confirmar'} tom="pend" />;
  return (
    <span className="data-parcela">
      {comDia(d.data)}
      <small className={`origem-data${d.pendencia ? ' a-confirmar' : ''}`}>{d.origem === 'informada' ? 'informada' : d.pendencia ? 'prevista · fim de semana: a confirmar' : 'prevista pela regra'}</small>
    </span>
  );
}

function useAcao(numero: string, aplicar: (x: Acompanhamento) => void) {
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const executar = async (f: () => Promise<Acompanhamento>, depois?: () => void) => {
    if (ocupado) return;
    setOcupado(true);
    setErro('');
    try {
      aplicar(await f());
      depois?.();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  void numero;
  return { erro, ocupado, executar, setErro };
}

function Rodape({ aoFechar, aoSalvar, ocupado, rotulo = 'Registrar', teste }: { aoFechar: () => void; aoSalvar: () => void; ocupado: boolean; rotulo?: string; teste: string }) {
  return (
    <>
      <button type="button" className="botao-secundario" onClick={aoFechar}>
        Cancelar
      </button>
      <button type="button" className="botao-primario" disabled={ocupado} aria-busy={ocupado} onClick={aoSalvar} data-teste={teste}>
        {rotulo}
      </button>
    </>
  );
}

type ModalLocacao = null | { tipo: 'etapa' | 'acompanhamento' | 'remessa' } | { tipo: 'datas'; remessa: string; parcela: number } | { tipo: 'estorno'; id: string } | { tipo: 'anular'; alvo: 'etapas' | 'remessas'; id: string; rotulo: string };

/**
 * P4 bloco 3 (I13, rev4): o acompanhamento da OL é carregado uma vez e as partes aparecem nos lugares da ficha
 * (acompanhamento, lateral de remessas e parcelas, histórico) — mesmos registros, regras e ações de antes.
 */
export function useLocacao(numero: string) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<Acompanhamento>(`/api/ordens/${numero}/locacao`), [numero]);
  const opcoes = useCarga(() => api.get<{ responsaveis: { id: string; nome: string }[] }>('/api/oportunidades/opcoes'), []);
  const [modal, setModal] = useState<ModalLocacao>(null);
  const acao = useAcao(numero, (x) => carga.setDados(x));
  const a = carga.dados;
  const podeRegistrar = Boolean(a) && pode('acompanhar_locacao', 'ordem') && a!.situacaoDocumento !== 'cancelada';
  return { numero, carga, a, opcoes, modal, setModal, fechar: () => setModal(null), acao, podeRegistrar, base: `/api/ordens/${numero}/locacao` };
}
export type CtxLocacao = ReturnType<typeof useLocacao>;

/** Remessas (NF) e parcelas — pagamento do Cliente ao parceiro e recebimento da Inttrix são registros distintos. */
export function ParcelasLocacao({ ctx }: { ctx: CtxLocacao }) {
  const { a, podeRegistrar, setModal } = ctx;
  if (!a) return null;
  const fin = a.financeiro;
  return (
    <>
      {a.remessas.length === 0 ? (
        <section className="bloco-impacto" data-teste="parcelas-ol">
          <h3>Parcelas</h3>
          <p className="sem-margem">Nenhuma parcela: a previsão começa após o registro da remessa (NF).</p>
        </section>
      ) : (
        a.remessas.map((r) => (
          <section key={r.id} className="bloco-impacto" data-teste={`parcelas-remessa-${r.nf}`}>
            <h3>
              Parcelas · remessa NF {r.nf} de {dataBr(r.dataRemessa)}
              {r.anulada && <SeloP texto={`Anulada: ${r.anulada.motivo}`} tom="ni" />}
              <Ajuda titulo="Parcelas da remessa">O prazo desta remessa é contado da NF de remessa. Informada: registrada a partir da fatura ou do aviso do parceiro. Prevista pela regra: calculada pela regra do parceiro. A confirmar: a regra ainda não define a data. O aluguel pago ao parceiro não é receita da Inttrix.</Ajuda>
            </h3>
            {!r.anulada && (
              <RolagemIndicada className="rolagem quadro-parcelas" teste={`parcelas-${r.nf}`}>
                <table className="tabela tabela-parcelas">
                  <thead>
                    <tr>
                      <th className="col-fixa">Parcela</th>
                      <th>Emissão</th>
                      <th>Vencimento</th>
                      <th>Aluguel</th>
                      {fin && (
                        <th className="rc-quebra">
                          Pago pelo Cliente <span className="rc-tag">Financeiro</span>
                        </th>
                      )}
                      <th className={fin ? 'rc-quebra' : undefined}>
                        {fin ? `Comissão ${pctBr(a.contrato.percentual)}${fin.comissao ? ` · ${fin.comissao}` : ''} ` : `Margem prevista ${pctBr(a.contrato.percentual)}`}
                        {fin && <span className="rc-tag">Financeiro</span>}
                      </th>
                      {podeRegistrar && <th>Datas</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {r.parcelas.map((p) => {
                      const f = p.financeiro;
                      return (
                        <tr key={p.numero} data-teste={`parcela-${r.nf}-${p.numero}`}>
                          <td className="col-fixa nowrap">{p.numero}ª</td>
                          <td>
                            <CelulaData d={p.emissao} />
                          </td>
                          <td>
                            <CelulaData d={p.vencimento} />
                          </td>
                          <td className="numero nowrap">{moeda2(p.aluguel)}</td>
                          {fin && <td className="rc-quebra" data-teste={`pago-financeiro-${r.nf}-${p.numero}`}>{f?.pagamentosCliente.length ? <SeloP texto={f.pagamentosCliente.map((m) => `${dataBr(m.data).slice(0, 5)} · ${moeda2(m.valor)}`).join(' + ')} tom="ok" /> : <SeloP texto="Não informado" tom="ni" />}</td>}
                          <td className={fin ? 'rc-quebra' : undefined} data-teste={`comissao-${r.nf}-${p.numero}`}>
                            {moeda2(f?.confirmado ?? f?.estimado ?? p.margemPrevista)}
                            {fin && (f ? <span className={`selo-previa ${f.tom} rc-l2`}>{f.disponibilidade ?? f.liberacao}</span> : <span className="rc-sec">comissão ainda não aberta no Financeiro</span>)}
                          </td>
                          {podeRegistrar && (
                            <td className="nowrap acoes-parcela">
                              <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'datas', remessa: r.id, parcela: p.numero })} data-teste={`datas-${r.nf}-${p.numero}`}>
                                Datas
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </RolagemIndicada>
            )}
            <p className="texto-secundario sem-margem">
              <b>Informada</b>: registrada a partir da fatura ou do aviso do parceiro · <b>Prevista pela regra</b>: calculada pela regra do parceiro · <b>A confirmar</b>: a regra ainda não define a data
            </p>
            <p className="texto-secundario sem-margem" data-teste="nota-financeiro">
              {fin ? 'Pagamento do Cliente e comissão: registrados no Contas a Receber · esta tela só exibe. ' : 'Acompanhamento financeiro (pagamento do Cliente e comissão): restrito ao Financeiro.'}
              {fin?.comissao && (
                <a href={`#/contas-receber/${fin.comissao}`} onClick={(e) => (e.preventDefault(), navegar(`/contas-receber/${fin.comissao}`))}>
                  Abrir {fin.comissao} no Contas a Receber
                </a>
              )}
            </p>
            {r.parcelas.some((p) => p.movimentos.length) && (
              <details className="acordeao-cotacoes">
                <summary>Registros anteriores ao Contas a Receber (só consulta e estorno)</summary>
                <ul className="lista-registros" data-teste={`movimentos-${r.nf}`}>
                  {r.parcelas.flatMap((p) =>
                    p.movimentos.map((m) => (
                      <li key={m.id} className={m.estorno ? 'anulado' : ''}>
                        {p.numero}ª · {m.tipo === 'pagamento_cliente' ? 'Pagamento do Cliente' : 'Recebimento da Inttrix'} · {dataBr(m.data)} · {moeda2(m.valor)} · {m.autor}
                        {m.estorno ? <span className="texto-secundario"> · estornado ({m.estorno.justificativa})</span> : podeRegistrar && (
                          <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'estorno', id: m.id })} data-teste={`estornar-${m.id}`}>
                            Estornar
                          </button>
                        )}
                      </li>
                    )),
                  )}
                </ul>
              </details>
            )}
            {podeRegistrar && !r.anulada && (
              <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'anular', alvo: 'remessas', id: r.id, rotulo: `a remessa NF ${r.nf}` })} data-teste={`anular-remessa-${r.nf}`}>
                Anular remessa
              </button>
            )}
          </section>
        ))
      )}

    </>
  );
}

/** Registros das etapas (com anulação justificada; nada é apagado). */
export function RegistrosEtapasLocacao({ ctx }: { ctx: CtxLocacao }) {
  const { a, podeRegistrar, setModal } = ctx;
  if (!a) return null;
  return (
    <>
        {a.registrosEtapa.length > 0 && (
          <details className="acordeao-cotacoes">
            <summary>Registros das etapas ({a.registrosEtapa.length})</summary>
            <ul className="lista-registros" data-teste="registros-etapa">
              {a.registrosEtapa.map((r) => (
                <li key={r.id} className={r.anulada ? 'anulado' : ''}>
                  {r.rotulo} · {dataBr(r.data)} · {r.autor}
                  {r.observacao ? ` — ${r.observacao}` : ''}
                  {r.anulada ? <span className="texto-secundario"> · anulado ({r.anulada.motivo})</span> : podeRegistrar && (
                    <button type="button" className="botao-link" onClick={() => setModal({ tipo: 'anular', alvo: 'etapas', id: r.id, rotulo: `o registro “${r.rotulo}” de ${dataBr(r.data)}` })} data-teste={`anular-etapa-${r.etapa}`}>
                      Anular
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
    </>
  );
}

/** Próxima ação e pendência da OL. */
export function ProximaAcaoLocacao({ ctx }: { ctx: CtxLocacao }) {
  const { a } = ctx;
  if (!a) return null;
  return (
    <>
        {a.proximaAcao ? (
          <p className="sem-margem" data-teste="proxima-acao-ol">
            <strong>Próxima ação:</strong> {a.proximaAcao.descricao} · {dataBr(a.proximaAcao.data)} · {a.proximaAcao.responsavel}
          </p>
        ) : (
          <p className="texto-secundario sem-margem">Sem próxima ação registrada.</p>
        )}
        {a.pendencia && (
          <p className="aviso aviso-alerta sem-margem" data-teste="pendencia-ol">
            Pendência: {a.pendencia}
          </p>
        )}
    </>
  );
}

export function ModaisLocacao({ ctx }: { ctx: CtxLocacao }) {
  const { a, modal, fechar, acao, base, opcoes } = ctx;
  if (!a) return null;
  return (
    <>
      {modal?.tipo === 'etapa' && <ModalEtapa base={base} acao={acao} aoFechar={fechar} />}
      {modal?.tipo === 'acompanhamento' && <ModalAcompanhamento base={base} a={a} responsaveis={opcoes.dados?.responsaveis ?? []} acao={acao} aoFechar={fechar} />}
      {modal?.tipo === 'remessa' && <ModalRemessa base={base} mensalidade={a.contrato.mensalidade} acao={acao} aoFechar={fechar} />}
      {modal?.tipo === 'datas' && <ModalDatas base={base} remessa={modal.remessa} parcela={modal.parcela} acao={acao} aoFechar={fechar} />}
      {modal?.tipo === 'estorno' && <ModalMotivo titulo="Estornar registro" rotulo="Justificativa do estorno *" ajuda="O registro é preservado; a parcela volta à situação anterior." caminho={`${base}/movimentos/${modal.id}/estorno`} campo="justificativa" acao={acao} aoFechar={fechar} teste="confirmar-estorno-locacao" />}
      {modal?.tipo === 'anular' && <ModalMotivo titulo={`Anular ${modal.rotulo}`} rotulo="Motivo *" ajuda="O registro anulado continua no histórico." caminho={`${base}/${modal.alvo}/${modal.id}/anular`} campo="motivo" acao={acao} aoFechar={fechar} teste="confirmar-anular" />}
    </>
  );
}

type Acao = ReturnType<typeof useAcao>;

function Erro({ acao }: { acao: Acao }) {
  return acao.erro ? (
    <p className="aviso aviso-erro" role="alert" data-teste="erro-locacao">
      {acao.erro}
    </p>
  ) : null;
}

function ModalEtapa({ base, acao, aoFechar }: { base: string; acao: Acao; aoFechar: () => void }) {
  const [etapa, setEtapa] = useState<IdEtapaLocacao>('po_enviada');
  const [data, setData] = useState(hojeIso());
  const [observacao, setObservacao] = useState('');
  return (
    <Modal titulo="Registrar etapa" aoFechar={aoFechar} rodape={<Rodape aoFechar={aoFechar} ocupado={acao.ocupado} teste="salvar-etapa" aoSalvar={() => void acao.executar(() => api.post(`${base}/etapas`, { etapa, data, observacao }), aoFechar)} />}>
      <Erro acao={acao} />
      <div className="grade-2">
        <label className="campo">
          Etapa *
          <select value={etapa} onChange={(e) => setEtapa(e.target.value as IdEtapaLocacao)} data-teste="etapa-locacao">
            {ETAPAS_REGISTRAVEIS.map((x) => (
              <option key={x} value={x}>
                {ROTULO_ETAPA[x]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Data do fato *
          <input type="date" value={data} max={hojeIso()} onChange={(e) => setData(e.target.value)} data-teste="data-etapa" />
        </label>
      </div>
      <label className="campo">
        Observação (retorno, pendência, documento)
        <input value={observacao} maxLength={1000} onChange={(e) => setObservacao(e.target.value)} data-teste="observacao-etapa" />
      </label>
      <p className="texto-secundario sem-margem">A remessa (NF) é registrada em “Registrar remessa”; a proposta aceita vem do Pedido.</p>
    </Modal>
  );
}

function ModalAcompanhamento({ base, a, responsaveis, acao, aoFechar }: { base: string; a: Acompanhamento; responsaveis: { id: string; nome: string }[]; acao: Acao; aoFechar: () => void }) {
  const { eu } = useSessao();
  const [descricao, setDescricao] = useState(a.proximaAcao?.descricao ?? '');
  const [data, setData] = useState(a.proximaAcao?.data ?? hojeIso());
  const [resp, setResp] = useState(a.proximaAcao?.responsavelId ?? eu?.id ?? responsaveis[0]?.id ?? '');
  const [pendencia, setPendencia] = useState(a.pendencia ?? '');
  const salvar = () => void acao.executar(() => api.put(`${base}/acompanhamento`, { proxima_acao: descricao.trim() ? { descricao: descricao.trim(), data, responsavel_id: resp } : null, pendencia: pendencia.trim() }), aoFechar);
  return (
    <Modal titulo="Próxima ação e pendência" aoFechar={aoFechar} largo rodape={<Rodape aoFechar={aoFechar} ocupado={acao.ocupado} rotulo="Salvar" teste="salvar-acompanhamento" aoSalvar={salvar} />}>
      <Erro acao={acao} />
      <div className="grade-3">
        <label className="campo">
          Próxima ação
          <input value={descricao} maxLength={300} onChange={(e) => setDescricao(e.target.value)} data-teste="proxima-acao-descricao" />
        </label>
        <label className="campo">
          Data
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} data-teste="proxima-acao-data" />
        </label>
        <label className="campo">
          Responsável
          <select value={resp} onChange={(e) => setResp(e.target.value)} data-teste="proxima-acao-responsavel">
            {responsaveis.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="campo">
        Pendência (aparece na lista de OL)
        <input value={pendencia} maxLength={1000} onChange={(e) => setPendencia(e.target.value)} data-teste="pendencia-descricao" />
      </label>
    </Modal>
  );
}

function ModalRemessa({ base, mensalidade, acao, aoFechar }: { base: string; mensalidade: number; acao: Acao; aoFechar: () => void }) {
  const [nf, setNf] = useState('');
  const [data, setData] = useState(hojeIso());
  const [quantidade, setQuantidade] = useState('1');
  const [valor, setValor] = useState(String(mensalidade).replace('.', ','));
  const [observacao, setObservacao] = useState('');
  const salvar = () => void acao.executar(() => api.post(`${base}/remessas`, { nf: nf.trim(), data_remessa: data, quantidade: Number(quantidade), valor_parcela: Number(valor.replace(/\./g, '').replace(',', '.')), observacao }), aoFechar);
  return (
    <Modal titulo="Registrar remessa (NF)" aoFechar={aoFechar} rodape={<Rodape aoFechar={aoFechar} ocupado={acao.ocupado} teste="salvar-remessa" aoSalvar={salvar} />}>
      <Erro acao={acao} />
      <div className="grade-2">
        <label className="campo">
          NF de remessa *
          <input value={nf} maxLength={40} onChange={(e) => setNf(e.target.value)} data-teste="nf-remessa" />
        </label>
        <label className="campo">
          Data da remessa *
          <input type="date" value={data} max={hojeIso()} onChange={(e) => setData(e.target.value)} data-teste="data-remessa" />
        </label>
        <label className="campo">
          Equipamentos nesta remessa
          <input inputMode="numeric" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} data-teste="quantidade-remessa" />
        </label>
        <label className="campo">
          Valor da parcela desta remessa (R$)
          <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} data-teste="valor-remessa" />
        </label>
      </div>
      <label className="campo">
        Observação
        <input value={observacao} maxLength={1000} onChange={(e) => setObservacao(e.target.value)} />
      </label>
      <p className="texto-secundario sem-margem">Remessa parcial: o prazo e as parcelas desta remessa contam desta NF; registre cada remessa separadamente.</p>
    </Modal>
  );
}

function ModalDatas({ base, remessa, parcela, acao, aoFechar }: { base: string; remessa: string; parcela: number; acao: Acao; aoFechar: () => void }) {
  const [emissao, setEmissao] = useState('');
  const [vencimento, setVencimento] = useState('');
  const [observacao, setObservacao] = useState('');
  return (
    <Modal titulo={`Datas efetivas da ${parcela}ª parcela`} aoFechar={aoFechar} rodape={<Rodape aoFechar={aoFechar} ocupado={acao.ocupado} rotulo="Informar" teste="salvar-datas" aoSalvar={() => void acao.executar(() => api.put(`${base}/remessas/${remessa}/parcelas/${parcela}`, { emissao: emissao || null, vencimento: vencimento || null, observacao }), aoFechar)} />}>
      <Erro acao={acao} />
      <div className="grade-2">
        <label className="campo">
          Emissão efetiva
          <input type="date" value={emissao} onChange={(e) => setEmissao(e.target.value)} data-teste="emissao-parcela" />
        </label>
        <label className="campo">
          Vencimento efetivo
          <input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} data-teste="vencimento-parcela" />
        </label>
      </div>
      <label className="campo">
        Observação (fatura, aviso do parceiro, ajuste pactuado)
        <input value={observacao} maxLength={1000} onChange={(e) => setObservacao(e.target.value)} />
      </label>
      <p className="texto-secundario sem-margem">As datas informadas prevalecem sobre a previsão da regra e ficam no histórico.</p>
    </Modal>
  );
}

function ModalMotivo({ titulo, rotulo, ajuda, caminho, campo, acao, aoFechar, teste }: { titulo: string; rotulo: string; ajuda: string; caminho: string; campo: 'motivo' | 'justificativa'; acao: Acao; aoFechar: () => void; teste: string }) {
  const [texto, setTexto] = useState('');
  return (
    <Modal titulo={titulo} aoFechar={aoFechar} rodape={<Rodape aoFechar={aoFechar} ocupado={acao.ocupado} rotulo="Confirmar" teste={teste} aoSalvar={() => void acao.executar(() => api.post(caminho, { [campo]: texto.trim() }), aoFechar)} />}>
      <Erro acao={acao} />
      <label className="campo">
        {rotulo}
        <input value={texto} maxLength={1000} onChange={(e) => setTexto(e.target.value)} data-teste="motivo-locacao" />
      </label>
      <p className="texto-secundario sem-margem">{ajuda}</p>
    </Modal>
  );
}
